import { IGetCcyConversionOrdersRequest } from "interfaces";
import { institutionalApi } from "./index";

import {
  IRouterOrder,
  IRouterCreateOrderRequest,
  IRouterCreatePayoutRequest,
  IRouterOrderRequest,
  IRouterOrderOriginal,
  IRouterOrderTimelineEvent,
  IRouterQuoteResponse,
  IRouterQuoteRequest,
} from "interfaces/router";
import { transformErrorResponse } from "utils";
import {
  PaymentCategories,
  RouterSupportedDOCountry,
  RouterSupportedFiat,
  SyntheticRouterSupportedFiat,
} from "interfaces/enums";

export const ccyConversionOrders = institutionalApi.injectEndpoints({
  endpoints: (builder) => ({
    getRouterOrders: builder.query<
      Array<IRouterOrder>,
      IGetCcyConversionOrdersRequest
    >({
      query: (params) => {
        return {
          url: `/router`,
          params,
          method: "GET",
        };
      },
      providesTags: ["RouterOrders"],
      transformErrorResponse,
      transformResponse: (response: Array<IRouterOrderOriginal>) => {
        return response.map((r) => ({
          ...r,
          ccy_source:
            r.country_dest === RouterSupportedDOCountry.US &&
            r.ccy_source === RouterSupportedFiat.USD
              ? SyntheticRouterSupportedFiat.MexDolar
              : r.ccy_source,
          qty_dest: String(r.qty_dest),
          qty_source: String(r.qty_source),
        })) as Array<IRouterOrder>;
      },
    }),
    getSuperBrokerRouterOrders: builder.query<
      Array<IRouterOrder>,
      IGetCcyConversionOrdersRequest
    >({
      query: (params) => {
        return {
          url: "/router/super_broker/orders",
          params,
          method: "GET",
        };
      },
      providesTags: ["RouterOrders"],
      transformErrorResponse,
      transformResponse: (response: Array<IRouterOrderOriginal>) => {
        return response.map((r) => ({
          ...r,
          ccy_source:
            r.country_dest === RouterSupportedDOCountry.US &&
            r.ccy_source === RouterSupportedFiat.USD
              ? SyntheticRouterSupportedFiat.MexDolar
              : r.ccy_source,
          qty_dest: String(r.qty_dest),
          qty_source: String(r.qty_source),
        })) as Array<IRouterOrder>;
      },
    }),
    getRouterOrder: builder.query<IRouterOrder, IRouterOrderRequest>({
      query: (params) => ({
        url: `/router/order/${params.order_id}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: "RouterOrder", id: arg.order_id },
      ],
      transformErrorResponse,
      transformResponse: (response: IRouterOrderOriginal) => {
        return {
          ...response,
          qty_dest: String(response.qty_dest),
          qty_source: String(response.qty_source),
        } as IRouterOrder;
      },
    }),
    getRouterOrderTimeline: builder.query<
      Array<IRouterOrderTimelineEvent>,
      IRouterOrderRequest
    >({
      query: (params) => ({
        url: `/router/order/${params.order_id}/timeline`,
        method: "GET",
      }),
      transformErrorResponse,
      providesTags: (_, __, params) => [
        { type: "OrderTimeline", id: params.order_id },
      ],
    }),
    createRouterPayout: builder.mutation<string, IRouterCreatePayoutRequest>({
      query: (body) => ({
        url: `/router/order/payout`,
        method: "POST",
        body,
      }),
      transformErrorResponse,
    }),
    confirmTransferSPID: builder.mutation<string, { order_id: string }>({
      query: (body) => ({
        url: `/router/order/spid/confirm_transfer`,
        method: "POST",
        body,
      }),
      transformErrorResponse,
    }),
    getOrderInvoiceDocument: builder.query<
      { url: string },
      { order_id: string }
    >({
      query: (params) => ({
        url: `/router/order/documents/invoice`,
        method: "GET",
        params,
      }),
      transformErrorResponse,
    }),
    createRouterOrder: builder.mutation<
      IRouterOrder,
      IRouterCreateOrderRequest
    >({
      query: (body) => ({
        url: `/router/order`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminOrders", "RouterOrders"],
      transformErrorResponse,
    }),
    createRouterQuoteBatch: builder.mutation<
      Array<{ data: IRouterQuoteResponse; error: Boolean; detail: string }>,
      Array<IRouterQuoteRequest>
    >({
      query: (body) => ({
        url: "/router/quote/batch",
        method: "POST",
        body,
      }),
      transformErrorResponse,
    }),
    requestCancellation: builder.mutation<
      undefined,
      { order_router_id: string }
    >({
      query: (body) => ({
        url: "/router/order/cancel",
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        "RouterOrders",
        { type: "RouterOrder", id: arg.order_router_id },
      ],
      transformErrorResponse,
    }),
    changeBeneficiary: builder.mutation<
      undefined,
      {
        order_router_id: string;
        destination_account_id: string;
        payment_category: PaymentCategories;
        memo?: string;
      }
    >({
      query: (body) => ({
        url: "/router/order/beneficiary_change",
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        "RouterOrders",
        { type: "RouterOrder", id: arg.order_router_id },
      ],
      transformErrorResponse,
    }),
    createRouterOrderBatch: builder.mutation<
      Array<{ data: IRouterOrder; error: Boolean; detail: string }>,
      Array<IRouterCreateOrderRequest>
    >({
      query: (body) => ({
        url: "/router/order/batch",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminOrders", "RouterOrders"],
      transformErrorResponse,
    }),
    ChangeClientExchangeRate: builder.mutation<
      undefined,
      {
        order_router_id: string;
        new_client_exchange_rate: number;
        rate_inverted: boolean;
      }
    >({
      query: (body) => ({
        url: "/router/order/modify_client_exchange_rate",
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        "RouterOrders",
        { type: "RouterOrder", id: arg.order_router_id },
      ],
      transformErrorResponse,
    }),
  }),
});

export const {
  useChangeClientExchangeRateMutation,
  useChangeBeneficiaryMutation,
  useRequestCancellationMutation,
  useCreateRouterQuoteBatchMutation,
  useCreateRouterOrderBatchMutation,
  useLazyGetOrderInvoiceDocumentQuery,
  useCreateRouterOrderMutation,
  useCreateRouterPayoutMutation,
  useGetRouterOrderQuery,
  useGetRouterOrderTimelineQuery,
  usePrefetch,
  useGetSuperBrokerRouterOrdersQuery,
  useConfirmTransferSPIDMutation,
  useGetRouterOrdersQuery,
} = ccyConversionOrders;
